import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  Loader2,
  Printer,
  TrendingUp,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { ErrorDeApi } from '@/shared/api/problema';
import type { ReporteDeEmisiones as Reporte } from '@/shared/api/tipos';
import { notificar } from '@/shared/ui/avisos';

export function ReporteDeEmisiones({ periodos }: { periodos: string[] }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [bajando, setBajando] = useState<'pdf' | 'xlsx' | null>(null);

  const reporte = useQuery({
    queryKey: ['emisiones-reporte', desde, hasta],
    queryFn: () =>
      api.get<Reporte>('/emisiones/reporte', {
        query: { desde: desde || undefined, hasta: hasta || undefined },
      }),
  });

  async function descargar(formato: 'pdf' | 'xlsx') {
    setBajando(formato);
    try {
      const parametros = new URLSearchParams();
      if (desde) parametros.set('desde', desde);
      if (hasta) parametros.set('hasta', hasta);
      const consulta = parametros.toString();

      const fichero = await api.descargar(
        `/emisiones/reporte.${formato}${consulta ? `?${consulta}` : ''}`,
      );
      if (!fichero) {
        notificar.error('NO SE PUDO GENERAR EL FICHERO');
        return;
      }

      const url = URL.createObjectURL(fichero);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = `emisiones-${desde || 'inicio'}_a_${hasta || 'hoy'}.${formato}`;
      enlace.click();
      URL.revokeObjectURL(url);

      notificar.exito(`Reporte descargado en ${formato.toUpperCase()}`);
    } catch (causa) {
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO DESCARGAR EL REPORTE',
      );
    } finally {
      setBajando(null);
    }
  }

  if (reporte.isLoading) {
    return (
      <Cargando
        titulo="Componiendo el reporte"
        detalle="Comparando lo emitido con lo que se debe"
        alto="h-72"
      />
    );
  }

  if (!reporte.data) {
    return (
      <p className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        NO SE PUDO GENERAR EL REPORTE
      </p>
    );
  }

  const r = reporte.data;
  const sinEmitir = r.periodos.length === 0;

  return (
    <div className="space-y-5">
      <div className="sin-imprimir flex flex-wrap items-end justify-between gap-4 rounded-2xl border border-armazon-200 bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="grid gap-1.5">
            <span className="text-[10px] font-semibold uppercase text-armazon-600">Desde</span>
            <select
              value={desde}
              onChange={(evento) => setDesde(evento.target.value)}
              className="h-10 rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-700 outline-none transition-colors focus:border-marca-400"
            >
              <option value="">EL PRIMERO</option>
              {periodos.map((periodo) => (
                <option key={periodo} value={periodo}>
                  {periodo}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1.5">
            <span className="text-[10px] font-semibold uppercase text-armazon-600">Hasta</span>
            <select
              value={hasta}
              onChange={(evento) => setHasta(evento.target.value)}
              className="h-10 rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-700 outline-none transition-colors focus:border-marca-400"
            >
              <option value="">EL ÚLTIMO</option>
              {periodos.map((periodo) => (
                <option key={periodo} value={periodo}>
                  {periodo}
                </option>
              ))}
            </select>
          </label>

          {(desde || hasta) && (
            <button
              type="button"
              onClick={() => {
                setDesde('');
                setHasta('');
              }}
              className="h-10 rounded-lg px-3 text-[11px] font-semibold uppercase text-armazon-500 transition-colors hover:bg-armazon-100"
            >
              Quitar filtro
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="mr-1 text-[10px] uppercase text-armazon-400">
            Al corte del {formatearLargo(r.corte)}
          </span>
          <button
            type="button"
            onClick={() => void descargar('pdf')}
            disabled={bajando !== null || sinEmitir}
            title="Descargar en PDF"
            aria-label="Descargar en PDF"
            className="grid size-10 place-items-center rounded-lg border border-armazon-200 bg-white text-armazon-600 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-40"
          >
            {bajando === 'pdf' ? (
              <Loader2 className="size-[18px] animate-spin" aria-hidden />
            ) : (
              <FileText className="size-[18px]" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => void descargar('xlsx')}
            disabled={bajando !== null || sinEmitir}
            title="Descargar en Excel"
            aria-label="Descargar en Excel"
            className="grid size-10 place-items-center rounded-lg border border-armazon-200 bg-white text-armazon-600 transition-colors hover:border-emerald-300 hover:text-emerald-600 disabled:opacity-40"
          >
            {bajando === 'xlsx' ? (
              <Loader2 className="size-[18px] animate-spin" aria-hidden />
            ) : (
              <FileSpreadsheet className="size-[18px]" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            title="Imprimir el reporte"
            aria-label="Imprimir el reporte"
            className="grid size-10 place-items-center rounded-lg border border-armazon-200 bg-white text-armazon-600 transition-colors hover:border-marca-300 hover:text-marca-600"
          >
            <Printer className="size-[18px]" aria-hidden />
          </button>
        </div>
      </div>

      {sinEmitir ? (
        <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
          {desde || hasta
            ? 'Ningún periodo emitido cae en ese rango'
            : 'Todavía no se ha emitido ningún mes'}
        </p>
      ) : (
        <div className="para-imprimir para-imprimir-largo space-y-5 rounded-2xl bg-white">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Indicador
              valor={r.emitido}
              rotulo="Emitido"
              nota={`${r.facturas.toLocaleString('es-EC')} facturas`}
            />
            <Indicador valor={r.cobrado} rotulo="Cobrado" nota="Pagos registrados" verde />
            <Indicador valor={r.porVencer} rotulo="Por vencer" nota="Todavía en plazo" />
            <Indicador
              valor={r.vencido}
              rotulo="Vencido"
              nota="Corre interés"
              alerta={r.vencido > 0}
            />
          </div>

          <div className="rounded-xl border border-armazon-200 p-4">
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-[11px] font-semibold uppercase text-armazon-600">
                De ${r.emitido.toFixed(2)} emitidos
              </span>
              <span className="importe text-sm font-bold uppercase text-armazon-800">
                ${r.deuda.toFixed(2)} en deuda
              </span>
            </div>
            <div className="flex h-3 overflow-hidden rounded-full bg-armazon-100">
              <Tramo valor={r.cobrado} total={r.emitido} clase="bg-emerald-500" rotulo="Cobrado" />
              <Tramo
                valor={r.porVencer}
                total={r.emitido}
                clase="bg-armazon-500"
                rotulo="Por vencer"
              />
              <Tramo valor={r.vencido} total={r.emitido} clase="bg-amber-500" rotulo="Vencido" />
            </div>
            <div className="mt-2 flex flex-wrap gap-4">
              <Leyenda clase="bg-emerald-500" texto="Cobrado" />
              <Leyenda clase="bg-armazon-500" texto="Por vencer" />
              <Leyenda clase="bg-amber-500" texto="Vencido" />
            </div>
          </div>

          {r.cobrado === 0 && (
            <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
              <p className="text-justify text-xs uppercase leading-relaxed text-blue-800">
                <span className="font-semibold">Todavía no hay pagos registrados.</span> El módulo
                de recaudación aún no existe, así que la columna «cobrado» estará en cero hasta que
                la ventanilla empiece a registrar cobros. Lo emitido y lo vencido sí son cifras
                reales.
              </p>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[11px]">
              <thead>
                <tr className="bg-armazon-800 text-white">
                  <Encabezado>Periodo</Encabezado>
                  <Encabezado>Vence</Encabezado>
                  <Encabezado className="text-right">Facturas</Encabezado>
                  <Encabezado className="text-right">Emitido</Encabezado>
                  <Encabezado className="text-right">Cobrado</Encabezado>
                  <Encabezado className="text-right">Por vencer</Encabezado>
                  <Encabezado className="text-right">Vencido</Encabezado>
                  <Encabezado className="text-right">Deuda</Encabezado>
                  <Encabezado className="text-right">Recup.</Encabezado>
                </tr>
              </thead>
              <tbody>
                {r.periodos.map((p) => (
                  <tr key={p.periodo} className="border-b border-armazon-100 even:bg-armazon-50/60">
                    <td className="importe px-2 py-2 font-semibold text-armazon-800">
                      {p.periodo}
                      {p.cuantasAnuladas > 0 && (
                        <span className="block text-[9px] uppercase text-armazon-400">
                          {p.cuantasAnuladas} anulada{p.cuantasAnuladas === 1 ? '' : 's'}
                        </span>
                      )}
                    </td>
                    <td className="importe px-2 py-2 text-armazon-600">
                      {formatearCorto(p.vencimiento)}
                    </td>
                    <td className="importe px-2 py-2 text-right text-armazon-700">
                      {p.facturas.toLocaleString('es-EC')}
                    </td>
                    <td className="importe px-2 py-2 text-right font-semibold text-armazon-800">
                      ${p.emitido.toFixed(2)}
                    </td>
                    <td
                      className={`importe px-2 py-2 text-right ${
                        p.cobrado > 0 ? 'font-semibold text-emerald-700' : 'text-armazon-300'
                      }`}
                    >
                      ${p.cobrado.toFixed(2)}
                    </td>
                    <td className="importe px-2 py-2 text-right text-armazon-600">
                      ${p.porVencer.toFixed(2)}
                    </td>
                    <td
                      className={`importe px-2 py-2 text-right ${
                        p.vencido > 0 ? 'font-semibold text-amber-700' : 'text-armazon-300'
                      }`}
                    >
                      ${p.vencido.toFixed(2)}
                    </td>
                    <td className="importe px-2 py-2 text-right font-semibold text-armazon-800">
                      ${p.deuda.toFixed(2)}
                    </td>
                    <td className="importe px-2 py-2 text-right text-armazon-600">
                      {p.recuperacion}%
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-armazon-100 font-semibold uppercase text-armazon-800">
                  <td className="px-2 py-2" colSpan={2}>
                    Total
                  </td>
                  <td className="importe px-2 py-2 text-right">
                    {r.facturas.toLocaleString('es-EC')}
                  </td>
                  <td className="importe px-2 py-2 text-right">${r.emitido.toFixed(2)}</td>
                  <td className="importe px-2 py-2 text-right">${r.cobrado.toFixed(2)}</td>
                  <td className="importe px-2 py-2 text-right">${r.porVencer.toFixed(2)}</td>
                  <td className="importe px-2 py-2 text-right">${r.vencido.toFixed(2)}</td>
                  <td className="importe px-2 py-2 text-right">${r.deuda.toFixed(2)}</td>
                  <td className="px-2 py-2" />
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="border-t border-armazon-200 pt-3 text-justify text-[10px] uppercase leading-relaxed text-armazon-500">
            Lo vencido se deduce de la fecha de vencimiento de cada factura al corte, no de un
            estado guardado: así no puede quedar desfasado. Las facturas anuladas no suman a ningún
            importe —se anularon para que no cuenten— pero se listan aparte, porque un mes con
            muchas anulaciones es una señal.
          </p>
        </div>
      )}
    </div>
  );
}

function Tramo({
  valor,
  total,
  clase,
  rotulo,
}: {
  valor: number;
  total: number;
  clase: string;
  rotulo: string;
}) {
  if (total <= 0 || valor <= 0) {
    return null;
  }
  return (
    <span
      className={clase}
      style={{ width: `${(valor / total) * 100}%` }}
      title={`${rotulo}: $${valor.toFixed(2)}`}
    />
  );
}

function Leyenda({ clase, texto }: { clase: string; texto: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[10px] uppercase text-armazon-500">
      <span className={`size-2 rounded-full ${clase}`} aria-hidden />
      {texto}
    </span>
  );
}

function Indicador({
  valor,
  rotulo,
  nota,
  verde = false,
  alerta = false,
}: {
  valor: number;
  rotulo: string;
  nota: string;
  verde?: boolean;
  alerta?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        alerta ? 'border-amber-200 bg-amber-50' : 'border-armazon-200'
      }`}
    >
      <span
        className={`importe block text-xl font-bold ${
          alerta ? 'text-amber-700' : verde ? 'text-emerald-700' : 'text-armazon-800'
        }`}
      >
        ${valor.toFixed(2)}
      </span>
      <span className="block text-[10px] font-semibold uppercase text-armazon-600">{rotulo}</span>
      <span className="block text-[9px] uppercase text-armazon-400">{nota}</span>
      {alerta && <TrendingUp className="mt-1 size-3 text-amber-600" aria-hidden />}
    </div>
  );
}

function Encabezado({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function formatearCorto(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}

function formatearLargo(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  const MESES = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];
  return `${Number(dia)} de ${MESES[Number(mes) - 1] ?? mes} de ${anio}`;
}
